import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import DOMPurify from "dompurify";

// If a legacy post is plain text (no HTML tags), preserve its paragraph breaks.
const renderBody = (body) => {
  if (!body) return "";
  const hasHtml = /<[a-z][\s\S]*?>/i.test(body);
  if (hasHtml) return DOMPurify.sanitize(body, { USE_PROFILES: { html: true } });
  return body
    .split(/\n{2,}/)
    .map((chunk) => `<p>${chunk.replace(/\n/g, "<br/>")}</p>`)
    .join("");
};

export default function BlogDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [slug]);
  if (!p) return <div className="container-kore py-40 text-kore-ink/50">Loading…</div>;
  return (
    <article className="container-kore py-20 max-w-3xl">
      <Seo
        title={p.title}
        description={p.excerpt || `${p.title} — an essay from the Kore Wellbeing journal.`}
        path={`/blog/${slug}`}
        image={p.cover_image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: p.title,
          image: p.cover_image ? [p.cover_image] : undefined,
          author: { "@type": "Person", name: p.author },
          publisher: { "@type": "Organization", name: "Kore Wellbeing" },
          articleSection: p.category,
        }}
      />
      <Link to="/blog" className="text-xs uppercase tracking-[0.2em] text-kore-gold">← Journal</Link>
      <div className="mt-6 text-[11px] uppercase tracking-[0.24em] text-kore-gold">{p.category}</div>
      <h1 className="display-1 mt-3 text-kore-forest">{p.title}</h1>
      <p className="mt-4 text-kore-ink/60 text-sm">By {p.author}</p>
      {p.cover_image && <img src={p.cover_image} alt={p.title} className="mt-10 rounded-3xl w-full aspect-[16/9] object-cover" />}
      <div
        data-testid="blog-body"
        className="prose prose-lg max-w-none mt-10 text-kore-ink/85 leading-relaxed font-light kore-blog-body"
        dangerouslySetInnerHTML={{ __html: renderBody(p.body) }}
      />
    </article>
  );
}
